import cn from 'classnames';
import { memo, useCallback } from 'react';

import {
  DEFAULT_TABLE_LIMIT,
  LIMIT_OPTIONS,
  LimitOption,
  useTableLimitsStore,
} from '@/shared/store/table-limits.store';

interface LimitSelectorProps {
  tableKey?: string;
  className?: string;
  compact?: boolean;
}

export const LimitSelector = memo(function LimitSelector({
  tableKey,
  className,
  compact = false,
}: LimitSelectorProps) {
  const globalLimit = useTableLimitsStore(s => s.globalLimit);
  const tableLimits = useTableLimitsStore(s => s.tableLimits);
  const setGlobalLimit = useTableLimitsStore(s => s.setGlobalLimit);
  const setTableLimit = useTableLimitsStore(s => s.setTableLimit);

  const currentLimit = tableKey ? (tableLimits[tableKey] ?? globalLimit) : globalLimit;
  const isUsingGlobal = tableKey ? !(tableKey in tableLimits) : true;

  const handleChange = useCallback(
    (value: LimitOption) => {
      if (tableKey) {
        setTableLimit(tableKey, value);
      } else {
        setGlobalLimit(value);
      }
    },
    [tableKey, setGlobalLimit, setTableLimit],
  );

  const isActive = (option: LimitOption) => currentLimit === option;
  const isUsingGlobalActive = (option: LimitOption) =>
    isActive(option) && isUsingGlobal && tableKey;

  return (
    <div className={cn('flex items-center', compact ? 'gap-1' : 'gap-2', className)}>
      {!compact && <span className="text-xs text-gray-2 whitespace-nowrap">Limit:</span>}
      <div className="flex items-center gap-0.5 bg-gray-700/50 rounded-md p-0.5">
        {LIMIT_OPTIONS.map(option => (
          <button
            key={option}
            type="button"
            className={cn(
              'rounded text-gray-300 transition-all duration-150',
              compact
                ? 'px-1.5 py-0.5 text-[10px] min-w-[22px]'
                : 'text-xs px-2 py-0.5 min-w-[28px]',
              isActive(option)
                ? isUsingGlobalActive(option)
                  ? 'bg-primary/70 text-white font-medium'
                  : 'bg-primary text-white font-medium'
                : 'hover:bg-gray-600/50 hover:text-gray-100',
            )}
            onClick={() => handleChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
      {tableKey && !isUsingGlobal && currentLimit !== globalLimit && (
        <span className="text-primary text-lg leading-none" title="Custom limit for this table">
          •
        </span>
      )}
    </div>
  );
});

export const useTableLimit = (tableKey: string): LimitOption => {
  return useTableLimitsStore(s => s.tableLimits[tableKey] ?? s.globalLimit);
};

export const useGlobalLimit = (): LimitOption => {
  return useTableLimitsStore(s => s.globalLimit);
};

export { DEFAULT_TABLE_LIMIT, LIMIT_OPTIONS, type LimitOption };
